import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send } from 'lucide-react';

/**
 * Compose modal for sending a purchase order to a supplier — mirrors the
 * invoice send flow (InvoiceEmailModal / InvoiceSMSModal): editable recipient,
 * subject (email only), and message, with Cancel/Send in the footer.
 * One component handles both channels via `mode`.
 */
interface PurchaseOrderSendModalProps {
  isOpen: boolean;
  mode: 'email' | 'sms';
  onClose: () => void;
  /** subject is only meaningful for email sends. */
  onSend: (params: { recipient: string; subject: string; message: string }) => void;
  recipient: string;
  supplierName: string;
  isSending?: boolean;
  initialSubject?: string;
  initialMessage?: string;
}

export default function PurchaseOrderSendModal({
  isOpen,
  mode,
  onClose,
  onSend,
  recipient: initialRecipient,
  supplierName,
  isSending = false,
  initialSubject,
  initialMessage,
}: PurchaseOrderSendModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setRecipient(initialRecipient || '');
      setSubject(initialSubject || '');
      setMessage(initialMessage || '');
    }
  }, [isOpen, initialRecipient, initialSubject, initialMessage]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const trimmedRecipient = recipient.trim();
  const recipientValid =
    mode === 'email'
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedRecipient)
      : trimmedRecipient.replace(/[^\d]/g, '').length >= 7;

  const canSend =
    !isSending && recipientValid && message.trim().length > 0 && (mode === 'sms' || subject.trim().length > 0);

  const handleSend = () => {
    if (!canSend) return;
    onSend({ recipient: trimmedRecipient, subject: subject.trim(), message });
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      // Portals bubble events through the React tree, not the DOM — without
      // this, clicks inside this modal reach the parent Order Summary
      // backdrop's onClick={onClose} and close everything.
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div
        className={`bg-white transform transition-all duration-200 fixed inset-0 w-full h-full sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[600px] sm:h-auto shadow-2xl ${
          isOpen
            ? 'translate-y-0 sm:translate-y-0 sm:translate-x-0 opacity-100'
            : 'translate-y-full sm:translate-y-0 sm:translate-x-full opacity-0 sm:opacity-100'
        }`}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
        onTransitionEnd={handleTransitionEnd}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <button
            onClick={onClose}
            className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{
              top: 'calc(1rem + env(safe-area-inset-top, 0px))',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <X className="h-5 w-5" />
          </button>

          <div
            className="bg-gray-50 border-b border-gray-200 px-6 py-6"
            style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}
          >
            <h2
              className="text-2xl font-bold text-gray-900 text-center"
              style={{ letterSpacing: '-0.02em' }}
            >
              {mode === 'email' ? 'Send Order Email' : 'Send Order SMS'}
            </h2>
            <p className="text-sm text-gray-500 text-center mt-1">To {supplierName}</p>
          </div>

          <div className="flex-1 overflow-y-auto bg-white" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="p-4 sm:p-6 space-y-5">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">To</label>
                <input
                  type={mode === 'email' ? 'email' : 'tel'}
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                  style={{ fontSize: '16px', minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                  placeholder={mode === 'email' ? 'supplier@email.com' : '+1 555 555 5555'}
                />
                {!recipientValid && trimmedRecipient.length > 0 && (
                  <p className="text-xs text-red-500">
                    {mode === 'email' ? 'Enter a valid email address.' : 'Enter a valid phone number.'}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Sends to this {mode === 'email' ? 'address' : 'number'} only — the supplier's saved contact info won't change.
                </p>
              </div>

              {mode === 'email' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                    style={{ fontSize: '16px', minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                    placeholder="Email subject"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  rows={mode === 'email' ? 8 : 10}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none touch-manipulation"
                  style={{ fontSize: '16px', WebkitTapHighlightColor: 'transparent' }}
                  placeholder="Enter your message"
                />
                <p className="text-xs text-gray-500">
                  {mode === 'email'
                    ? 'The purchase order details (items, comments, and totals) will be included below your message automatically.'
                    : 'This is the exact text the supplier will receive. Edit it before sending.'}
                </p>
              </div>
            </div>
          </div>

          <div
            className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4"
            style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!canSend}
                className={`flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                  canSend
                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSending ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="h-4 w-4 mr-2" />
                    {mode === 'email' ? 'Send Email' : 'Send SMS'}
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
